// Order mirrors the segmented control — the segment index casts to this enum.
enum IntegratorMappingSection
{
    keys,
    entities,
    valueTypes,
    rules,
    targets,
    grid
}

class IntegratorMappingDetailViewController extends MUIViewController
{
    static newInstance(): IntegratorMappingDetailViewController {
        let vc = new IntegratorMappingDetailViewController("integrator-mapping-detail-view");
        vc.initWithResource("layout/setup/IntegratorMappingDetailView.html");
        return vc;
    }

    private addButton: MUIButton = null;
    private backButton: MUIButton = null;
    private saveButton: MUIButton = null;
    private titleLabel: MUILabel = null;
    private segmentedControl: MUISegmentedControl = null;
    private col1Label: MUILabel = null;
    private col2Label: MUILabel = null;
    private col3Label: MUILabel = null;
    private tableView: UITableView = null;

    private selectedSection = IntegratorMappingSection.keys;

    // The translation grid, fetched from the integrator server when the segment opens.
    // Flattened for the table: one entry per row, sections = areas.
    private _grid: any = null;
    private _gridSections: any[][] = [];

    private _mappingInfo: IntegratorMappingInfo = null;
    set mappingInfo(item: IntegratorMappingInfo) {
        this._mappingInfo = item;
        this.updateUI();
    }

    viewDidLoad() {
        super.viewDidLoad();

        this.backButton = MUIOutlet(this, "back-btn", "MUIButton");
        this.backButton.setAction(this, function() {
            this.navigationController.popViewController(true);
        });

        this.saveButton = MUIOutlet(this, "save-btn", "MUIButton");
        this.saveButton.setAction(this, function(){
            DBHelper.saveMainContext();
        });

        this.titleLabel = MUIOutlet(this, "title-lbl", "MUILabel");

        this.segmentedControl = MUIOutlet(this, "segmented-ctl", "MUISegmentedControl");
        this.segmentedControl.setAction(this, function(this:IntegratorMappingDetailViewController, control:MUISegmentedControl, index:number) {
            this.selectedSection = index as IntegratorMappingSection;
            this.updateUI();
        });

        this.col1Label = MUIOutlet(this, "col1-lbl", "MUILabel");
        this.col2Label = MUIOutlet(this, "col2-lbl", "MUILabel");
        this.col3Label = MUIOutlet(this, "col3-lbl", "MUILabel");

        this.addButton = MUIOutlet(this, "add-btn", "MUIButton");
        this.addButton.setAction(this, function(this:IntegratorMappingDetailViewController) {
            if (this.selectedSection == IntegratorMappingSection.rules) this.addMappingRule();
            else if (this.selectedSection == IntegratorMappingSection.valueTypes) this.addMappingValueType();
            else this.addMappingKey();
        });

        this.tableView = MUIOutlet(this, "table-view", "UITableView");
        this.tableView.dataSource = this;
        this.tableView.delegate = this;
        // Editing stays on for the whole screen: it shows the delete accessories and
        // enables the drag-reorder handles (value types — same pattern as the
        // dashboard edit screen).
        this.tableView.setEditing(true);

        this.updateUI();
    }

    viewWillAppear(animate?) {
        super.viewWillAppear(animate);
        this.updateUI();
    }

    private updateUI() {
        if (this.viewIsLoaded == false || this._mappingInfo == null) return;

        this.titleLabel.text = this._mappingInfo.name;

        // Keys, rules and value types are editable here; entity/target rows come from
        // the integrator setup endpoints.
        this.addButton.setHidden(this.selectedSection != IntegratorMappingSection.keys
                              && this.selectedSection != IntegratorMappingSection.rules
                              && this.selectedSection != IntegratorMappingSection.valueTypes);

        if (this.selectedSection != IntegratorMappingSection.rules) { this.ruleTokenContext = null; this._categoryNames = null; }
        if (this.selectedSection == IntegratorMappingSection.rules) this.loadRuleTokenContext();
        if (this.selectedSection == IntegratorMappingSection.grid) this.loadGrid();

        this.updateColumnTitles();
        this.updateSectionStyle();

        this._ruleValueTypes = null;
        this.fetchedResultsController = null;
        this.tableView.reloadData();
    }

    private updateColumnTitles() {
        switch (this.selectedSection) {
            case IntegratorMappingSection.keys:
                if (this.isMatrixMapping()) {
                    this.col1Label.text = MIOLocalizeString("TAG", "TAG");
                    let def = this.defaultCategoryName();
                    this.col2Label.text = MIOLocalizeString("CATEGORY", "CATEGORY") + (def ? " — " + MIOLocalizeString("NO_TAG_MATCH", "NO TAG / NO MATCH") + " → " + def : "");
                }
                else {
                    this.col1Label.text = MIOLocalizeString("SOURCE_KEY", "SOURCE KEY");
                    this.col2Label.text = MIOLocalizeString("TARGET_KEY", "TARGET KEY");
                }
                this.col3Label.text = "";
                break;

            case IntegratorMappingSection.entities:
                this.col1Label.text = MIOLocalizeString("SOURCE_ENTITY", "SOURCE ENTITY");
                this.col2Label.text = MIOLocalizeString("SOURCE_ID", "SOURCE ID");
                this.col3Label.text = MIOLocalizeString("TARGET", "TARGET");
                break;

            case IntegratorMappingSection.targets:
                this.col1Label.text = MIOLocalizeString("TARGET", "TARGET");
                this.col2Label.text = MIOLocalizeString("USER_INFO", "USER INFO");
                this.col3Label.text = "";
                break;

            case IntegratorMappingSection.rules:
                this.col1Label.text = MIOLocalizeString("RULE", "RULE");
                this.col2Label.text = MIOLocalizeString("CONDITION_FIRST_MATCH_WINS", "CONDITION — FIRST MATCH WINS, TOP TO BOTTOM");
                this.col3Label.text = MIOLocalizeString("VALUE", "VALUE");
                break;

            case IntegratorMappingSection.grid:
                this.col1Label.text = "";
                this.col2Label.text = "";
                this.col3Label.text = "";
                break;

            case IntegratorMappingSection.valueTypes:
                this.col1Label.text = MIOLocalizeString("VALUE_TYPE", "VALUE TYPE");
                this.col2Label.text = MIOLocalizeString("TITLE_DRAG_TO_REORDER", "TITLE — DRAG ROWS TO REORDER");
                this.col3Label.text = MIOLocalizeString("RESULT_KIND", "RESULT");
                break;
        }
    }

    private updateSectionStyle() {
        let layer = this.view.layer;
        layer.classList.remove("segment-keys", "segment-entities", "segment-targets", "segment-rules", "segment-valuetypes");
        switch (this.selectedSection) {
            case IntegratorMappingSection.keys:       layer.classList.add("segment-keys"); break;
            case IntegratorMappingSection.entities:   layer.classList.add("segment-entities"); break;
            case IntegratorMappingSection.targets:    layer.classList.add("segment-targets"); break;
            case IntegratorMappingSection.rules:      layer.classList.add("segment-rules"); break;
            case IntegratorMappingSection.valueTypes: layer.classList.add("segment-valuetypes"); break;
            case IntegratorMappingSection.grid:       layer.classList.add("segment-grid"); break;
        }
    }

    // The rules FRC groups sections alphabetically by valueType; the display order
    // follows the vocabulary (the pipeline: identity → classification → Opera codes).
    private ruleFRCSection(displaySection: number): number {
        if (this.selectedSection != IntegratorMappingSection.rules) return displaySection;
        let frc = this.fetchedResultsController;
        let vocab = this.ruleValueTypes();
        let order: {frc: number, vocab: number}[] = [];
        for (let i = 0; i < frc.sections.length; i++) {
            let first = frc.objectAtIndexPath(MIOIndexPath.indexForRowInSection(0, i)) as IntegratorMappingRule;
            let v = vocab.length;
            for (let j = 0; j < vocab.length; j++) { if (first != null && vocab[j].type == first.valueType) { v = j; break; } }
            order.push({frc: i, vocab: v});
        }
        order.sort((a, b) => a.vocab - b.vocab);
        return order[displaySection] != null ? order[displaySection].frc : displaySection;
    }

    private ruleKind(valueType: string): string {
        let kind = this.inferValueKind(valueType);
        for (let vt of this.ruleValueTypes()) { if (vt.type == valueType) { kind = vt.valueKind; break; } }
        return kind;
    }

    // Display rows of a rules section: one row per rule, and for multiple-results
    // rules (categorySplit) one indented sub-row per split part right under it
    // (part == -1 marks the rule row itself).
    private ruleRows(displaySection: number): {rule: IntegratorMappingRule, part: number}[] {
        let frcSection = this.ruleFRCSection(displaySection);
        let count = this.fetchedResultsController.sections[frcSection].numberOfObjects();
        let rows: {rule: IntegratorMappingRule, part: number}[] = [];
        for (let i = 0; i < count; i++) {
            let rule = this.fetchedResultsController.objectAtIndexPath(MIOIndexPath.indexForRowInSection(i, frcSection)) as IntegratorMappingRule;
            rows.push({rule: rule, part: -1});
            if (this.ruleKind(rule.valueType) == "categorySplit") {
                let parts = OperaRuleSplit.parts(rule.value).length;
                for (let p = 0; p < parts; p++) rows.push({rule: rule, part: p});
            }
        }
        return rows;
    }

    numberOfSections(tableview) {
        if (this.selectedSection == IntegratorMappingSection.grid) return this._gridSections.length;
        return this.fetchedResultsController.sections.length;
    }

    numberOfRowsInSection(tableview, section) {
        if (this.selectedSection == IntegratorMappingSection.grid) return this._gridSections[section].length;
        if (this.selectedSection == IntegratorMappingSection.rules) return this.ruleRows(section).length;
        let sec = this.fetchedResultsController.sections[this.ruleFRCSection(section)];
        return sec.numberOfObjects();
    }

    cellAtIndexPath(tableview, indexPath: MIOIndexPath) {
        if (this.selectedSection == IntegratorMappingSection.grid) {
            let entry = this._gridSections[indexPath.section][indexPath.row];
            let cell = tableview.dequeueReusableCellWithIdentifier(entry.kind) as MatrixGridCell;
            if (entry.kind == "MatrixGridHeaderCell") cell.setHeader(entry.data);
            else if (entry.kind == "MatrixGridCodeCell") cell.setCode(entry.data);
            else cell.setPayment(entry.data);
            return cell;
        }

        if (this.selectedSection == IntegratorMappingSection.rules) {
            let entry = this.ruleRows(indexPath.section)[indexPath.row];
            let rule = entry.rule;
            let kind = this.ruleKind(rule.valueType);
            let options = kind == "category" || kind == "categorySplit" ? this.categoryNames()
                        : kind == "revenueCenter" ? this.revenueCenterNames() : [];

            if (entry.part >= 0) {
                let cell = tableview.dequeueReusableCellWithIdentifier("OperaMappingRuleSplitPartCell") as OperaMappingRuleSplitPartCell;
                cell.valueOptions = options;
                cell.setPart(rule, entry.part);
                return cell;
            }

            let cell = tableview.dequeueReusableCellWithIdentifier("OperaMappingRuleCell") as OperaMappingRuleCell;
            cell.tokenContext = this.ruleTokenContext;
            cell.valueKind = kind;
            cell.valueOptions = options;
            cell.onSplitStructureChange = () => this.tableView.reloadData();
            cell.item = rule;
            return cell;
        }

        let item = this.fetchedResultsController.objectAtIndexPath(indexPath);

        switch (this.selectedSection) {
            case IntegratorMappingSection.entities: {
                let cell = tableview.dequeueReusableCellWithIdentifier("IntegratorMappingEntityCell") as IntegratorMappingEntityCell;
                cell.item = item as IntegratorMappingEntity;
                return cell;
            }

            case IntegratorMappingSection.valueTypes: {
                let cell = tableview.dequeueReusableCellWithIdentifier("IntegratorMappingValueTypeCell") as IntegratorMappingValueTypeCell;
                cell.item = item as IntegratorMappingValueType;
                return cell;
            }

            case IntegratorMappingSection.targets: {
                let cell = tableview.dequeueReusableCellWithIdentifier("IntegratorMappingTargetCell") as IntegratorMappingTargetCell;
                cell.item = item as IntegratorMappingTargetEntity;
                return cell;
            }

            default: {
                // Key editor: bank fixed list by type, the matrix tag → category editor
                // when the mapping IS a matrix (see isMatrixMapping), generic two-field
                // editor for everything else.
                if (this._mappingInfo.type == "bank-importer") {
                    let cell = tableview.dequeueReusableCellWithIdentifier("BankMappingKeyCell") as BankMappingKeyCell;
                    cell.item = item as IntegratorMappingKey;
                    return cell;
                }
                if (this.isMatrixMapping()) {
                    let cell = tableview.dequeueReusableCellWithIdentifier("OperaMappingKeyCell") as OperaMappingKeyCell;
                    cell.categories = this.categoryNames();
                    cell.item = item as IntegratorMappingKey;
                    return cell;
                }
                let cell = tableview.dequeueReusableCellWithIdentifier("IntegratorMappingDetailCell") as IntegratorMappingDetailCell;
                cell.item = item as IntegratorMappingKey;
                return cell;
            }
        }
    }

    // Whether this mapping is a PMS mapping matrix is decided by the mapping itself, never
    // by an integration's name: its seed declares one "category:<NAME>" key per category,
    // whoever owns it (opera, ulyses, the next PMS). The "<integration>-matrix" type family
    // is honoured as well, so the editor is already right on the first paint — before the
    // keys reach the main context — and for mappings seeded by older servers.
    private isMatrixMapping(): boolean {
        let type = this._mappingInfo.type;
        if (type != null && type.endsWith("-matrix")) return true;
        return this.categoryNames().length > 0;
    }

    private defaultCategoryName(): string {
        let predicate = MIOPredicate.predicateWithFormat("deletedAt == null && targetKey == 'default' && mappingInfo.identifier == '" + this._mappingInfo.identifier + "'");
        let keys = DBHelper.queryObjectsFromMainContext("IntegratorMappingKey", null, predicate) as IntegratorMappingKey[];
        for (let key of keys) {
            if (key.sourceKey && key.sourceKey.indexOf("category:") == 0) return key.sourceKey.substring(9);
        }
        return null;
    }

    // The integration's rule valueType vocabulary: the mapping info's
    // IntegratorMappingValueType rows (name + display title, ordered). Falls back to
    // the valueTypes already used by this mapping's rules, then to the Opera defaults
    // — so old seeds keep working until they are re-run.
    private _ruleValueTypes: {type:string, label:string, valueKind:string}[] = null;
    private inferValueKind(type: string): string {
        if (type == "revenueCenter") return "revenueCenter";
        if (type == "category") return "category";
        if (type == "categorySplit") return "categorySplit";
        return "code";
    }

    private ruleValueTypes(): {type:string, label:string, valueKind:string}[] {
        if (this._ruleValueTypes != null) return this._ruleValueTypes;

        let ret: {type:string, label:string, valueKind:string}[] = [];
        let sortDescriptors = [MIOSortDescriptor.sortDescriptorWithKey("orderIndex", true)];
        let predicate = MIOPredicate.predicateWithFormat("deletedAt == null && mappingInfo.identifier == '" + this._mappingInfo.identifier + "'");
        let types = DBHelper.queryObjectsFromMainContext("IntegratorMappingValueType", sortDescriptors, predicate) as IntegratorMappingValueType[];
        for (let t of types) {
            if (t.name == null) continue;
            let ui = t.userInfo;
            let kind = ui != null && ui["valueKind"] != null ? ui["valueKind"] : this.inferValueKind(t.name);
            ret.push({type: t.name, label: t.title || t.name, valueKind: kind});
        }

        if (ret.length == 0) {
            let rules = DBHelper.queryObjectsFromMainContext("IntegratorMappingRule", null, predicate) as IntegratorMappingRule[];
            let seen: {[key:string]: boolean} = {};
            for (let r of rules) {
                if (r.valueType != null && seen[r.valueType] != true) { seen[r.valueType] = true; ret.push({type: r.valueType, label: r.valueType, valueKind: this.inferValueKind(r.valueType)}); }
            }
        }

        if (ret.length == 0) {
            // Vocabulary not loaded yet — answer with the defaults but DON'T cache,
            // so the real rows take over as soon as they arrive.
            return [ {type: "transactionCode", label: "Transaction codes", valueKind: "code"},
                     {type: "paymentCode",     label: "Payment codes",     valueKind: "code"},
                     {type: "paymentRoom",     label: "Payment rooms",     valueKind: "code"},
                     {type: "category",        label: "Categories",        valueKind: "category"} ];
        }

        this._ruleValueTypes = ret;
        return ret;
    }

    // The value pickers' vocabularies, read from what the screen already holds — never
    // a query per cell: a query on this store asks the server, the answer is a change,
    // the change reloads the table and its cells query again, dozens of requests per
    // save or delete (seen on the rules screen: thousands of IntegratorMappingRule /
    // IntegratorMappingKey fetches).
    private revenueCenterNames(): string[] {
        let names: string[] = [];
        let frc = this._fetchedResultsController;
        if (frc == null || this.selectedSection != IntegratorMappingSection.rules) return names;
        for (let section of frc.sections) {
            for (let obj of section.objects) {
                let r = obj as IntegratorMappingRule;
                if (r.valueType == "revenueCenter" && r.value != null && r.value.length > 0 && names.indexOf(r.value) < 0) names.push(r.value);
            }
        }
        return names.sort();
    }

    // The mapping's category: keys, read once with the token context (the keys are
    // edited on their own segment, never from a rule row).
    private _categoryNames: string[] = null;
    private categoryNames(): string[] {
        if (this._categoryNames != null) return this._categoryNames;
        let predicate = MIOPredicate.predicateWithFormat("deletedAt == null && mappingInfo.identifier == '" + this._mappingInfo.identifier + "'");
        let keys = DBHelper.queryObjectsFromMainContext("IntegratorMappingKey", null, predicate) as IntegratorMappingKey[];
        let names: string[] = [];
        for (let key of keys) {
            if (key.sourceKey && key.sourceKey.indexOf("category:") == 0) names.push(key.sourceKey.substring(9));
        }
        this._categoryNames = names;
        return names;
    }

    didSelectCellAtIndexPath(tableView, indexPath: MIOIndexPath) {
        // selection handled inline via cell controls
    }

    // Rule sections carry the valueType — shown with the same split as the review doc,
    // titled from the integration's declared vocabulary (ruleValueType: keys).
    // MIOFetchSection has no name property: read the valueType off the first row.
    titleForHeaderInSection(tableView: UITableView, section: number) {
        if (this.selectedSection != IntegratorMappingSection.rules) return null;
        let first = this.fetchedResultsController.objectAtIndexPath(MIOIndexPath.indexForRowInSection(0, this.ruleFRCSection(section))) as IntegratorMappingRule;
        if (first == null) return null;
        for (let vt of this.ruleValueTypes()) {
            if (vt.type == first.valueType) return vt.label;
        }
        return first.valueType;
    }

    editingStyleForRowAtIndexPath(tableView: UITableView, indexPath: MIOIndexPath) {
        // Grid rows are projections of rules and seeded cells — nothing to delete here;
        // removal happens where the rows live (rules tab, or re-seed).
        if (this.selectedSection == IntegratorMappingSection.grid) return UITableViewCellEditingStyle.None;
        return UITableViewCellEditingStyle.Delete;
    }

    // MARK: - Drag & drop reordering (rules and value types)

    // Order is never typed — the visual order IS the evaluation order (rules: first
    // match wins) and the vocabulary order (dropdowns, group order).
    canMoveRowAtIndexPath(tableView: UITableView, indexPath: MIOIndexPath): boolean {
        if (this.selectedSection == IntegratorMappingSection.valueTypes) return true;
        if (this.selectedSection != IntegratorMappingSection.rules) return false;
        let entry = this.ruleRows(indexPath.section)[indexPath.row];
        return entry != null && entry.part < 0;    // split parts live under their rule
    }

    private sectionObjects(section: number): MIOManagedObject[] {
        let count = this.fetchedResultsController.sections[section].numberOfObjects();
        let items: MIOManagedObject[] = [];
        for (let i = 0; i < count; i++) {
            items.push(this.fetchedResultsController.objectAtIndexPath(MIOIndexPath.indexForRowInSection(i, section)) as MIOManagedObject);
        }
        return items;
    }

    // Rules reorder only inside their own group — a rule's valueType is what the
    // integration's code asks for, dragging it elsewhere would be meaningless.
    targetIndexPathForMoveFromRowAtIndexPath(tableView: UITableView, from: MIOIndexPath, proposed: MIOIndexPath): MIOIndexPath {
        if (proposed.section == from.section) return proposed;
        let lastRow = this.ruleRows(from.section).length - 1; // dragged row excluded
        return MIOIndexPath.indexForRowInSection(proposed.section < from.section ? 0 : lastRow, from.section);
    }

    moveRowAtIndexPath(tableView: UITableView, from: MIOIndexPath, to: MIOIndexPath) {
        if (this.selectedSection == IntegratorMappingSection.valueTypes) {
            let items = this.sectionObjects(0) as IntegratorMappingValueType[];
            let moved = items[from.row];
            if (moved == null) return;
            items.splice(from.row, 1);
            items.splice(to.row, 0, moved);
            for (let i = 0; i < items.length; i++) items[i].orderIndex = i;
            this._ruleValueTypes = null;
            return;
        }

        if (this.selectedSection != IntegratorMappingSection.rules) return;
        if (from.section != to.section) return;

        // Display rows include the split sub-rows — translate back to rule order:
        // the destination rule index is the number of rule rows before the drop
        // position once the dragged row is taken out.
        let rows = this.ruleRows(from.section);
        let entry = rows[from.row];
        if (entry == null || entry.part >= 0) return;
        let moved = entry.rule;

        let remaining = rows.filter(r => r.rule != moved);
        let toRow = Math.min(to.row, remaining.length);
        let toIndex = 0;
        for (let i = 0; i < toRow; i++) { if (remaining[i].part < 0) toIndex++; }

        let items = this.sectionObjects(this.ruleFRCSection(from.section)) as IntegratorMappingRule[];
        items.splice(items.indexOf(moved), 1);
        items.splice(toIndex, 0, moved);
        for (let i = 0; i < items.length; i++) items[i].orderIndex = i;

        // A rule dragged past another rule's sub-rows leaves them visually split
        // until the data reasserts itself.
        this.tableView.reloadData();
    }

    commitEditingStyleForRowAtIndexPath(tableView, editingStyle: UITableViewCellEditingStyle, indexPath: MIOIndexPath) {
        if (editingStyle != UITableViewCellEditingStyle.Delete) return;
        if (this.selectedSection == IntegratorMappingSection.grid) return;

        if (this.selectedSection == IntegratorMappingSection.rules) {
            let entry = this.ruleRows(indexPath.section)[indexPath.row];
            if (entry == null) return;
            if (entry.part >= 0) {
                let parts = OperaRuleSplit.parts(entry.rule.value);
                parts.splice(entry.part, 1);
                entry.rule.value = OperaRuleSplit.serialize(parts);
                this.tableView.reloadData();
                return;
            }
            DBHelper.deleteObjectFromMainContext(entry.rule, true);
            return;
        }

        let item = this.fetchedResultsController.objectAtIndexPath(indexPath) as MIOManagedObject;
        DBHelper.deleteObjectFromMainContext(item, true);
    }

    private _fetchedResultsController: MIOFetchedResultsController = null;
    set fetchedResultsController(value) {
        if (value == null && this._fetchedResultsController != null)
            this._fetchedResultsController.delegate = null;
        this._fetchedResultsController = value;
    }

    get fetchedResultsController() {
        if (this._fetchedResultsController != null) return this._fetchedResultsController;
        if (this._mappingInfo == null) return null;

        let ad = MUIWebApplication.sharedInstance().delegate;

        let fetchRequest: MIOFetchRequest = null;
        switch (this.selectedSection) {
            case IntegratorMappingSection.entities: {
                let sortDescriptors = [
                    MIOSortDescriptor.sortDescriptorWithKey("sourceEntityName", true),
                    MIOSortDescriptor.sortDescriptorWithKey("sourceID", true)
                ];
                let predicate = "deletedAt == null && mappingInfo.identifier == '" + this._mappingInfo.identifier + "'";
                fetchRequest = DBHelper.listFetchRequestWithEntityName("IntegratorMappingEntity", sortDescriptors, predicate);
                fetchRequest.relationshipKeyPathsForPrefetching = ["targetEntity"];
                break;
            }

            case IntegratorMappingSection.targets: {
                // Targets are shared rows (no mappingInfo relation): list them all.
                let sortDescriptors = [
                    MIOSortDescriptor.sortDescriptorWithKey("targetEntityName", true),
                    MIOSortDescriptor.sortDescriptorWithKey("targetID", true)
                ];
                fetchRequest = DBHelper.listFetchRequestWithEntityName("IntegratorMappingTargetEntity", sortDescriptors, "deletedAt == null");
                break;
            }

            case IntegratorMappingSection.rules: {
                // Grouped by valueType, ordered by orderIndex within each group —
                // evaluation order: the first matching rule wins.
                let sortDescriptors = [
                    MIOSortDescriptor.sortDescriptorWithKey("valueType", false),
                    MIOSortDescriptor.sortDescriptorWithKey("orderIndex", true)
                ];
                let predicate = "deletedAt == null && mappingInfo.identifier == '" + this._mappingInfo.identifier + "'";
                fetchRequest = DBHelper.listFetchRequestWithEntityName("IntegratorMappingRule", sortDescriptors, predicate);
                break;
            }

            case IntegratorMappingSection.valueTypes: {
                let sortDescriptors = [MIOSortDescriptor.sortDescriptorWithKey("orderIndex", true)];
                let predicate = "deletedAt == null && mappingInfo.identifier == '" + this._mappingInfo.identifier + "'";
                fetchRequest = DBHelper.listFetchRequestWithEntityName("IntegratorMappingValueType", sortDescriptors, predicate);
                break;
            }

            default: {
                let sortDescriptors = [MIOSortDescriptor.sortDescriptorWithKey("sourceKey", true)];
                let predicate = "deletedAt == null && mappingInfo.identifier == '" + this._mappingInfo.identifier + "'";
                // A mapping matrix: only the tag mappings are venue-editable; the category
                // declarations are structure owned by the seed.
                if (this.isMatrixMapping()) predicate += " && sourceKey CONTAINS 'tag:'";
                fetchRequest = DBHelper.listFetchRequestWithEntityName("IntegratorMappingKey", sortDescriptors, predicate);
                break;
            }
        }

        let frc = new MIOFetchedResultsController();
        let sectionKeyPath = this.selectedSection == IntegratorMappingSection.rules ? "valueType" : null;
        frc.initWithFetchRequest(fetchRequest, ad.managedObjectContext, sectionKeyPath);
        frc.delegate = this;
        frc.performFetch();

        this._fetchedResultsController = frc;
        return this._fetchedResultsController;
    }

    // Entity catalogs behind the friendly token syntax (#BA:, #DESK:, #PM:, #TAG:,
    // #CARD:) — id ↔ name in both directions plus the suggestion pools. Pay methods,
    // business areas and cashdesks are small catalogs, fetched once when the rules
    // segment is first shown and kept for the screen's lifetime.
    private ruleTokenContext: RuleTokenContext = null;
    private loadRuleTokenContext() {
        if (this.ruleTokenContext != null) return;

        let ctx: RuleTokenContext = {
            byId: {},
            byKindName: {BA: {}, DESK: {}, PM: {}, MENU: {}, PRODUCT: {}},
            names: {BA: [], DESK: [], PM: [], MENU: [], PRODUCT: []},
            tags: [],
            subtypes: ["visa", "mc", "amex"]
        };
        this.ruleTokenContext = ctx;

        let vc = this;

        // The vocabulary drives the group order and the value editors — make sure the
        // rows are actually loaded (the TYPES segment may never have been visited).
        let vtPredicate = MIOPredicate.predicateWithFormat("deletedAt == null && mappingInfo.identifier == '" + this._mappingInfo.identifier + "'");
        DBHelper.queryObjectsWithCompletion("IntegratorMappingValueType", null, vtPredicate, [], this, function(objects) {
            vc._ruleValueTypes = null;
            if (vc.selectedSection == IntegratorMappingSection.rules) vc.tableView.reloadData();
        });

        let kinds = [ {entity: "BusinessArea", kind: "BA"}, {entity: "CashDesk", kind: "DESK"}, {entity: "PayMethod", kind: "PM"}, {entity: "Menu", kind: "MENU"}, {entity: "Product", kind: "PRODUCT"} ];
        for (let k of kinds) {
            DBHelper.queryObjectsWithCompletion(k.entity, null, MIOPredicate.predicateWithFormat("deletedAt == null"), [], this, function(objects) {
                for (let obj of objects) {
                    let id = obj.valueForKey("identifier");
                    let name = obj.valueForKey("name");
                    if (id == null || name == null) continue;
                    ctx.byId[id.toUpperCase()] = {kind: k.kind, name: name};
                    ctx.byKindName[k.kind][name.toUpperCase()] = id.toUpperCase();
                    ctx.names[k.kind].push(name);
                }
                ctx.names[k.kind].sort();
                if (vc.selectedSection == IntegratorMappingSection.rules) vc.tableView.reloadData();
            });
        }

        // Tag suggestions and the category vocabulary come from the mapping's own keys,
        // read once here.
        let predicate = MIOPredicate.predicateWithFormat("deletedAt == null && mappingInfo.identifier == '" + this._mappingInfo.identifier + "'");
        let keys = DBHelper.queryObjectsFromMainContext("IntegratorMappingKey", null, predicate) as IntegratorMappingKey[];
        let categories: string[] = [];
        for (let key of keys) {
            if (key.sourceKey && key.sourceKey.indexOf("tag:") == 0) ctx.tags.push(key.sourceKey.substring(4));
            if (key.sourceKey && key.sourceKey.indexOf("category:") == 0) categories.push(key.sourceKey.substring(9));
        }
        ctx.tags.sort();
        this._categoryNames = categories;
    }

    // Never rebuild the FRC here: a fresh fetch asks the server, whose answer is a
    // change, which would fetch again — an endless stream of requests on every save
    // or delete (seen on the rules screen).
    controllerDidChangeContent(controller) {
        this.tableView.reloadData();
    }

    // A new rule asks for its group (rows don't move between groups) and joins it
    // after the last rule — never in front of existing ones (first match wins, an
    // accidental early catch-all would shadow everything below it).
    private addMappingRule() {
        let avc = new MUIAlertViewController();
        avc.initWithTitle(
            MIOLocalizeString("NEW_RULE", "NEW RULE"),
            MIOLocalizeString("SELECT_THE_RULE_GROUP", "SELECT THE RULE GROUP"),
            MUIAlertViewStyle.Default
        );

        let types = this.ruleValueTypes();
        avc.addComboBoxWithConfigurationHandler(this, function(comboBox: MUIComboBox) {
            // addItem(text, value): the label is shown, the type is what the rule stores.
            // Reversed, the rule's valueType was the label and the server never ran it.
            for (let vt of types) comboBox.addItem(vt.label, vt.type);
        });

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("ADD", "ADD"), MUIAlertActionStyle.Default, this, function(this:IntegratorMappingDetailViewController) {
            let valueType = avc.comboBoxes[0].getSelectedItem() || types[0].type;

            let maxOrder = -1;
            let predicate = MIOPredicate.predicateWithFormat("deletedAt == null && valueType == '" + valueType + "' && mappingInfo.identifier == '" + this._mappingInfo.identifier + "'");
            let rules = DBHelper.queryObjectsFromMainContext("IntegratorMappingRule", null, predicate) as IntegratorMappingRule[];
            for (let r of rules) { if (r.orderIndex > maxOrder) maxOrder = r.orderIndex; }

            let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
            let entity = MIOEntityDescription.insertNewObjectForEntityForName("IntegratorMappingRule", ad.managedObjectContext) as IntegratorMappingRule;
            entity.identifier = MIOUUID.UUID().UUIDString;
            entity.valueType = valueType;
            entity.orderIndex = maxOrder + 1;
            entity.name = MIOLocalizeString("NEW_RULE", "NEW RULE");
            entity.rule = null;
            entity.value = "";
            entity.mappingInfo = this._mappingInfo;
            ad.managedObjectContext.save();
        }));

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CANCEL", "CANCEL"), MUIAlertActionStyle.Cancel, null, null));

        this.presentViewController(avc, true);
    }

    private addMappingValueType() {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;

        let maxOrder = -1;
        let predicate = MIOPredicate.predicateWithFormat("deletedAt == null && mappingInfo.identifier == '" + this._mappingInfo.identifier + "'");
        let types = DBHelper.queryObjectsFromMainContext("IntegratorMappingValueType", null, predicate) as IntegratorMappingValueType[];
        for (let t of types) { if (t.orderIndex > maxOrder) maxOrder = t.orderIndex; }

        let entity = MIOEntityDescription.insertNewObjectForEntityForName("IntegratorMappingValueType", ad.managedObjectContext) as IntegratorMappingValueType;
        entity.identifier = MIOUUID.UUID().UUIDString;
        entity.name = MIOLocalizeString("NEW_VALUE_TYPE", "newValueType");
        entity.title = "";
        entity.orderIndex = maxOrder + 1;
        entity.mappingInfo = this._mappingInfo;
        ad.managedObjectContext.save();
    }

    // The grid comes from the integrator server (the same loader and provenance the
    // money paths use), flattened for the table: sections = areas; row 0 the header,
    // then the transaction codes, then the payment rows when the matrix has that axis.
    private loadGrid() {
        let ws = MUIWebApplication.sharedInstance().delegate.webService;
        ws.integratorMatrixGrid(this._mappingInfo, (json: any, error: any) => {
            if (error != null || json == null) {
                AppHelper.showErrorMessage(this, MIOLocalizeString("GRID", "GRID"), error ?? MIOLocalizeString("NO_RESPONSE", "NO RESPONSE"));
                return;
            }
            this._grid = json;
            this._gridSections = (json["areas"] ?? []).map((area: any) => {
                let rows: any[] = [{ kind: "MatrixGridHeaderCell", data: area }];
                for (let entry of area["transaction"] ?? []) rows.push({ kind: "MatrixGridCodeCell", data: entry });
                for (let entry of area["payments"] ?? []) rows.push({ kind: "MatrixGridPaymentCell", data: entry });
                return rows;
            });
            if (this.selectedSection == IntegratorMappingSection.grid) this.tableView.reloadData();
        });
    }

    private addMappingKey() {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let entity = MIOEntityDescription.insertNewObjectForEntityForName("IntegratorMappingKey", ad.managedObjectContext) as IntegratorMappingKey;
        entity.identifier = MIOUUID.UUID().UUIDString;
        if (this.isMatrixMapping()) {
            entity.sourceKey = "tag:";
            entity.targetKey = this.categoryNames()[0] || "";
        }
        else {
            entity.sourceKey = MIOLocalizeString("SOURCE KEY", "SOURCE KEY");
            entity.targetKey = MIOLocalizeString("TARGET KEY", "TARGET KEY");
        }
        entity.mappingInfo = this._mappingInfo;
        ad.managedObjectContext.save();
    }
}
